/**
 * Compact timeline of prior progress logs for a task.
 * Shown in the ActivityProgressWizard header when prior logs exist.
 * @agentic connections
 */

import {
  Badge,
  Collapse,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconClockHour4,
  IconPhone,
  IconMail,
  IconMessage,
  IconNote,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import type { Communication, Observation } from "@medplum/fhirtypes";
import { formatDate } from "@medplum/core";
import {
  buildProgressTimeline,
  type ProgressTimelineEntry,
} from "./buildProgressTimeline";

/**
 * Props for ProgressLogHistory.
 * @agentic connections
 */
export type ProgressLogHistoryProps = {
  logs: Communication[];
  /**
   * Care-time Observations for the same Task. Merged into the timeline and
   * deduped against the Communications they were tee'd from. Defaults to none.
   */
  careTimeLogs?: Observation[];
};

/**
 * Returns an icon for the communication medium code.
 * @agentic connections
 */
const getMediumIcon = (mediumCode: string | undefined) => {
  switch (mediumCode) {
    case "phone":
      return <IconPhone size={14} />;
    case "email":
      return <IconMail size={14} />;
    case "text":
      return <IconMessage size={14} />;
    default:
      return <IconNote size={14} />;
  }
};

/**
 * Renders a compact timeline of prior progress log interactions.
 * @agentic connections
 */
export const ProgressLogHistory = ({
  logs,
  careTimeLogs = [],
}: ProgressLogHistoryProps) => {
  const [expanded, setExpanded] = useState(false);
  const entries = useMemo(
    () => buildProgressTimeline(logs, careTimeLogs),
    [logs, careTimeLogs],
  );
  const showCollapse = entries.length > 3;
  const visibleEntries = showCollapse ? entries.slice(0, 3) : entries;
  const collapsedEntries = showCollapse ? entries.slice(3) : [];

  if (entries.length === 0) return null;

  const renderCommunication = (
    entry: Extract<ProgressTimelineEntry, { kind: "communication" }>,
  ) => {
    const { communication: log, minutes } = entry;
    const outcomeCode =
      log.reasonCode?.[0]?.coding?.[0]?.display ??
      log.reasonCode?.[0]?.coding?.[0]?.code ??
      "Progress";
    const mediumCode = log.medium?.[0]?.coding?.[0]?.code;
    const sentDate = log.sent ? formatDate(log.sent) : "Unknown date";
    const noteText = log.payload?.[0]?.contentString;

    return (
      <Group key={`comm-${log.id}`} gap="xs" align="flex-start" wrap="nowrap">
        {getMediumIcon(mediumCode)}
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs">
            <Badge size="xs" variant="light" color="gray">
              {outcomeCode}
            </Badge>
            {minutes ? (
              <Badge size="xs" variant="light" color="purple">
                {minutes} min
              </Badge>
            ) : null}
            <Text size="xs" c="dimmed">
              {sentDate}
            </Text>
          </Group>
          {noteText && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {noteText}
            </Text>
          )}
        </Stack>
      </Group>
    );
  };

  const renderCareTime = (
    entry: Extract<ProgressTimelineEntry, { kind: "care-time" }>,
  ) => {
    const { observation, minutes } = entry;
    const methodDisplay =
      observation.method?.coding?.[0]?.display ?? "Care time";
    const timestamp = observation.effectiveDateTime ?? observation.issued;
    const date = timestamp ? formatDate(timestamp) : "Unknown date";
    const noteText = observation.note?.[0]?.text;

    return (
      <Group
        key={`obs-${observation.id}`}
        gap="xs"
        align="flex-start"
        wrap="nowrap"
      >
        <IconClockHour4 size={14} />
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs">
            <Badge size="xs" variant="light" color="purple">
              {minutes} min
            </Badge>
            <Text size="xs" c="dimmed">
              {methodDisplay}
            </Text>
            <Text size="xs" c="dimmed">
              {date}
            </Text>
          </Group>
          {noteText && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {noteText}
            </Text>
          )}
        </Stack>
      </Group>
    );
  };

  const renderEntry = (entry: ProgressTimelineEntry) =>
    entry.kind === "communication"
      ? renderCommunication(entry)
      : renderCareTime(entry);

  return (
    <Paper p="sm" withBorder radius="md" bg="gray.0">
      <Stack gap="xs">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
          Prior Logs ({entries.length})
        </Text>

        {visibleEntries.map(renderEntry)}

        {showCollapse && (
          <Collapse in={expanded}>
            <Stack gap="xs">{collapsedEntries.map(renderEntry)}</Stack>
          </Collapse>
        )}

        {showCollapse && (
          <UnstyledButton onClick={() => setExpanded((v) => !v)}>
            <Group gap={4}>
              {expanded ? (
                <IconChevronUp size={14} />
              ) : (
                <IconChevronDown size={14} />
              )}
              <Text size="xs" c="purple">
                {expanded
                  ? "Show less"
                  : `Show ${collapsedEntries.length} more`}
              </Text>
            </Group>
          </UnstyledButton>
        )}
      </Stack>
    </Paper>
  );
};
